
import { VoyageShoreContextModelBase } from "./voyage-shore-context-model";
import { VesselsTrackingModel } from "./vessels-tracking-model";
import { VoyagePlansModel } from "./voyage-plans-model";
import { VoyFleetAlarmModel } from "./voy-fleet-alarm-model";
import { TerminalsModel } from "./terminals-model";
import { regionsMap } from "./terminals-model-impl";
import { VesselInfo } from "./types/vessels-model.types";
import { CurrentScheduleState } from "./types/voyage-schedules-model.types";
import { UiStateModel, UiStateModelParam, FiSubView } from "./ui-state-model";
import { VesselsModel } from "./vessels-model";
import { VoyageSchedulesModel } from "./voyage-schedules-model";
import { AlarmEvents, NotificationEvents, VesselRank } from "./types/voy-fleet-alarm-model.types";
import { TerminalInfo, PortInfo, Region } from "./types/terminals-model.types";
import { ShoreContextInternalCache, ShoreContextEvent, ListView } from "./types/voyage-shore-context-model.types";
import { nonMaerskWithBox } from './types/vessels-model.types'
import { FilterUtils } from "../../local_modules/voy-global-filters/src/filter-utils";
import { OperatorId, VoyageFilterType, FilterType, LocationId } from "../../local_modules/voy-global-filters/src/types";
import { VoyFleetNotificationModel } from "./voy-fleet-notification-model";

export class VoyageShoreContextModelImpl extends VoyageShoreContextModelBase {
    constructor() {
        super();

        this._setVessels = this._setVessels.bind(this);
        this._setTerminals = this._setTerminals.bind(this);
        this._setOperatorAndServiceCode = this._setOperatorAndServiceCode.bind(this);
        this._onVesselsUpdated = this._onVesselsUpdated.bind(this);

        this._filterVessels = this._filterVessels.bind(this);
        this._omniFilterVessel = this._omniFilterVessel.bind(this);
        this._sortVessels = this._sortVessels.bind(this);
        this._getAlarmsForVessel = this._getAlarmsForVessel.bind(this);

        this._filterTerminals = this._filterTerminals.bind(this);
        this._omnitFilterTerminals = this._omnitFilterTerminals.bind(this);
        this._applyWorkerResult = this._applyWorkerResult.bind(this);

        this._plotAndTrackVessel = this._plotAndTrackVessel.bind(this);

        // Set Internal caching
        this._setVessels();
        this._setTerminals();
        this._setOperatorAndServiceCode();

        // Initial plotting of fleet and potential vessel if imo url parm exists.
        this._plotAndTrackVessel();

        // Update vessles
        VoyFleetAlarmModel?.addEventListener(AlarmEvents.AlarmsUpdated, this._setVessels);
        VoyFleetNotificationModel?.addEventListener(NotificationEvents.NotificationsUpdated, this._setVessels);
        VoyFleetAlarmModel?.addEventListener(AlarmEvents.AlarmRanksUpdated, this._setVessels);
        VesselsModel!.addEventListener('maersk-vessels-updated', this._onAlarmsUpdated);
        UiStateModel!.addEventListener(`${UiStateModelParam.PORT}-change`, this._setVessels);
        UiStateModel!.addEventListener(`${UiStateModelParam.PortRegion}-change`, this._setVessels);
        UiStateModel!.addEventListener(`${UiStateModelParam.Search}-change`, this._setVessels);

        // Update vessels, service and operator codes
        VesselsModel!.addEventListener('vessels-updated', this._onVesselsUpdated);

        // Update terminals
        TerminalsModel!.addEventListener('terminals-updated', this._setTerminals);
        UiStateModel!.addEventListener(`${UiStateModelParam.Search}-change`, this._setTerminals);
        UiStateModel!.addEventListener(`${UiStateModelParam.PORT}-change`, this._setTerminals);
        UiStateModel!.addEventListener(`${UiStateModelParam.PortRegion}-change`, this._setTerminals);

        // When a single vessel is selected
        UiStateModel!.addEventListener(`${UiStateModelParam.IMO}-change`, this._plotAndTrackVessel);
    }

    /**
     * _cache
     * This cache is used to read the current state of a certain data type,
     * it is assumed the data stored here is filtered and sorted based
     * on the requirements for the use case. 
     * 
     * The public getters should get their values from here.
     * 
     * The cache is updated on user input (eg. serach and filters) and
     * updates to the underlaying data models eg. Vessels, Terminals, Positions etc.
     */
    protected _cache: ShoreContextInternalCache = {
        vessels: {
            list: []
        },
        terminals: {
            map: new Map<string, TerminalInfo>(),
            list: []
        },
        ports: new Map<string, PortInfo>(),
        regions: [], 
        services: {
            map: new Map<string, Array<number>>(),
            list: []
        },
        operators: {
            map: new Map<string, Array<number>>(),
            list: []
        }
    }

    private _vesselsByServiceCode: Map<string, Array<number>> = new Map();
    private _shouldUpdateFilterMapView: boolean = false;

    get vessels(): VesselInfo[] {
        return this._cache.vessels.list;
    }

    get services(): Array<string> {
        return this._cache.services.list;
    }

    get servicesMap(): Map<string, Array<number>> {
        return this._cache.services.map;
    }

    get operators(): Array<string> {
        return this._cache.operators.list;
    }

    get operatorsMap(): Map<string, Array<number>> {
        return this._cache.operators.map;
    }

    get vesselsByServiceCodes(): Map<string, Array<number>> {
        return this._vesselsByServiceCode;
    }

    get terminals(): TerminalInfo[] {
        return this._cache.terminals.list;
    }
    get terminalsMap(): Map<string, TerminalInfo> {
        return this._cache.terminals.map;
    }

    get ports(): Map<string, PortInfo> {
        return this._cache.ports;
    }

    get regions(): Region[] {
        return this._cache.regions;
    }

    get shouldUpdateFilterMapView(): boolean {
        return this._shouldUpdateFilterMapView;
    }

    public setShouldUpdateFilterMapView(shouldUpdate: boolean) {
        this._shouldUpdateFilterMapView = true;
    }

    private _filterVesselsWorker: Worker|null = null;

    private _setOperatorAndServiceCode() {
        const vesselsBySC = new Map();
        const vesselsByOC = new Map();

        // create maps
        VesselsModel!.maerskVessels.forEach((vessel, imo) => {
            if (vesselsBySC.has(imo)) {
                vesselsBySC.get(vessel.serviceCode).push(imo);
            } else {
                vesselsBySC.set(vessel.serviceCode, [imo]);
            }

            if (vesselsByOC.has(vessel.operatorCode)) {
                vesselsByOC.get(vessel.operatorCode).push(imo);
            } else {
                vesselsByOC.set(vessel.operatorCode, [imo]);
            }
        })

        this._cache.operators.map = vesselsByOC;
        this._cache.services.map = vesselsBySC;

        this._cache.services.list = Array.from(this._cache.services.map.keys()).filter(key => key !== null).sort((a, b) => a.localeCompare(b))
        this._cache.operators.list = Array.from(this._cache.operators.map.keys())

        this.dispatchEvent(new CustomEvent(ShoreContextEvent.ServiceCodeChange));
    }

    private _onVesselsUpdated() {
        this._setVessels();
        this._setOperatorAndServiceCode();
    }

    private _setVessels(): void {
        const omniFilter = FilterUtils.stringToFilter(UiStateModel?.getValue('q') ?? "");
        const showNonMaersk = omniFilter?.commands.find(el => el.vals?.includes(OperatorId.NonMSK.toLowerCase()) || el.cmd?.includes('type') || el.cmd?.includes('ais')|| el.cmd?.includes('ig'));
        const globalFilterMetadataMaerskVessels = VesselsModel!.globalFilterMetadataMaerskVessels;
        const globalFilterMetadataNonMaerskVessels = VesselsModel!.globalFilterMetadataNonMaerskVessels;

        // Generic filtering tools
        const scheduleStates = VoyageSchedulesModel?.currentScheduleStates;
        const isPortListView = ListView.Ports === UiStateModel!.getValue(UiStateModelParam.ListView);
        
        const ranks = VoyFleetAlarmModel!.vesselRanks;

        const workerPayload = {
            omniFilter,
            showNonMaersk,
            nonMaerskVesselsWithImo: globalFilterMetadataNonMaerskVessels,
            maerskVesselsWithImo: globalFilterMetadataMaerskVessels,
            scheduleStates,
            ranks
        };

        // The Worker is not being terminated. It's a feature that is frequently used,
        // so avoid creating the worker on every search input by letting the worker persist.
        if (!this._filterVesselsWorker) {
            this._filterVesselsWorker = new Worker('./web-workers/global-filter.worker.js', { type: `module` });
            this._filterVesselsWorker.addEventListener("message", (e) => {
                const {imoNumbers: filteredImos} = e.data;
               
                this._applyWorkerResult(filteredImos, ranks, omniFilter,);
            })
            this._filterVesselsWorker.postMessage(workerPayload);
       
        } else {
            this._filterVesselsWorker.postMessage(workerPayload);
        }
    }

    private _applyWorkerResult( filteredImos: number[], ranks: Map<number, VesselRank>, omniFilter?: VoyageFilterType, ) {
        let filteredVessels: VesselInfo[] = []

        filteredImos.forEach(imo => {
            if (VesselsModel?.nonMaerskVessels.has(imo)) {
                filteredVessels.push(VesselsModel!.nonMaerskVessels.get(imo)!)
            }
            if (VesselsModel?.maerskVessels.has(imo)) {
                filteredVessels.push(VesselsModel!.maerskVessels.get(imo)!)
            }
        })

        filteredVessels = filteredVessels.sort(this._sortVesselsFromWebworker(ranks));
        this._cache.vessels.list = filteredVessels;

        //we don't have to update positions if we have terminals filter on 'tml'
        if(omniFilter?.commands.find(el => el.cmd === "tml")){
            return;
        }

        VesselsTrackingModel!.setIMOList(filteredImos);
        this.dispatchEvent(new CustomEvent(ShoreContextEvent.VesselsChange));
    }

    private _sortVesselsFromWebworker( ranks: Map<number, VesselRank>) {
        return (vesselA: VesselInfo, vesselB: VesselInfo): number => {
            const v1Ranks = ranks.get(vesselA.imoNo);
            const v2Ranks = ranks.get(vesselB.imoNo);
    
            // Handle if vessel name is null
            if (!vesselA.vesselName || !vesselB.vesselName) return 1;
    
            const sortedByName = vesselA.vesselName.toLowerCase().localeCompare(vesselB.vesselName.toLowerCase());
    
            if (!ranks) return sortedByName;
    
            if (!v1Ranks && v2Ranks) return 1;
            if (v1Ranks && !v2Ranks) return -1;
    
            // If both vessel have ranks sort them on latest alarm and rankweigth
            if (v1Ranks && v2Ranks) {
                if (v1Ranks.rankWeight === v2Ranks.rankWeight) {
                    return new Date(v1Ranks!.latestAlarmTs).getTime() - new Date(v2Ranks!.latestAlarmTs).getTime();
                }
    
                return v1Ranks.rankWeight > v2Ranks.rankWeight ? -1 : 1;
            }
         
            return sortedByName;
        }
    
    }

    _onAlarmsUpdated() {
        if (!this._cache) {
            return;
        }

        for (const vessel of this._cache.vessels.list) {
            vessel.alarms = VoyFleetAlarmModel!.getVesselAlarmsBasicInfo(vessel.imoNo);
        }
        //TODO: Change dispatch event to indicate only maersk vessels have changed
        //TODO: Make fleet rendering re-render only maersk vessels based on this.
        this.dispatchEvent(new CustomEvent(ShoreContextEvent.VesselsChange));
    }

    private _setTerminals(): void {
        const terminals = Array.from(TerminalsModel!.terminals.values())
            .filter(this._filterTerminals);
        const regions = regionsMap.filter(this._filterRegions);       
        this._cache.terminals.list = terminals;
        this._cache.terminals.map.clear();
        this._cache.regions = regions;
        this._cache.ports.clear();

        terminals.forEach(terminal => {
            this._cache.terminals.map.set(terminal.locationId, terminal);

            const portExsists = this._cache.ports.has(terminal.cityId);
            if (portExsists) {
                this._cache.ports.get(terminal.cityId)?.terminals.push(terminal);
            } else {
                const port: PortInfo = {
                    city: terminal.city,
                    cityId: terminal.cityId,
                    terminals: [terminal]
                };
                this._cache.ports.set(terminal.cityId, port);
            }
        })

        this.dispatchEvent(new CustomEvent(ShoreContextEvent.TerminalsChange));
    }

    private _filterRegions(region: Region) {
        let showTerminal = true;
        const omniFilter = FilterUtils.stringToFilter(UiStateModel?.getValue('q') ?? "");

        omniFilter?.text?.forEach(f => {
            showTerminal = region.name.toLowerCase().includes(f) || (region.locations.find(el => el.toLowerCase().includes(f)) ? true : false)
        });
        
        return showTerminal;
    }

    private _filterVessels(vessel: VesselInfo): boolean {   
        let showVesselPosition = true;

        // Generic filtering tools
        const scheduleStates = VoyageSchedulesModel?.currentScheduleStates;
        const vesselSchedule = scheduleStates?.find(state => state.imoNo === vessel.imoNo)?.schedule;
        const omniFilter = FilterUtils.stringToFilter(UiStateModel?.getValue('q') ?? "");

        switch (true) {
            case (!this._omniFilterVessel(vessel, omniFilter, vesselSchedule)):
                showVesselPosition = false;
                break;
            default:
                break;
        }
        return showVesselPosition;
    }

    private _getAlarmsForVessel(vessel: VesselInfo) {
        const alarms = VoyFleetAlarmModel!.getVesselAlarmsBasicInfo(vessel.imoNo);
        return { ...vessel, alarms };
    }

    private _omniFilterVessel(vessel: VesselInfo, omniFilter?: VoyageFilterType, vesselSchedule?: CurrentScheduleState): boolean {
        let showVessel = true;
        if (!omniFilter) {
            return showVessel;
        }

        // Filter commands
        omniFilter.commands.forEach(fcmd => {
            if (showVessel && fcmd.cmd === FilterType.FleetGroups) {

                showVessel = vessel.fg ? fcmd.vals!.includes(vessel.fg) : false;
            }

            if (showVessel && fcmd.cmd === FilterType.Location) {
                showVessel = vesselSchedule?.inPort ? fcmd.vals!.includes(LocationId.INPORT) : fcmd.vals!.includes(LocationId.ONVOYAGE);
            }

            if (showVessel && fcmd.cmd === FilterType.Service) {
                showVessel = vessel.serviceCode ? fcmd.vals!.includes(vessel.serviceCode.toLowerCase()) : false;
            }
            if (showVessel && fcmd.cmd === FilterType.VesselClass) {
                showVessel = vessel.vesselClass ? fcmd.vals!.includes(vessel.vesselClass.toLowerCase()) : false;
            }

            if (showVessel && fcmd.cmd === FilterType.Operator) {
                let nonMaerskWithMaerskBox = false;
                if(vessel.ownershipType && nonMaerskWithBox.includes(vessel.ownershipType)) nonMaerskWithMaerskBox = true
               

                switch (true) {
                   
                    case fcmd.vals!.includes(OperatorId.NonMSKWithBox.toLowerCase()) && nonMaerskWithMaerskBox:
                        showVessel = true;
                        break;
                    case vessel.operatorCode !== null && fcmd.vals!.includes(vessel.operatorCode.toLowerCase()):
                        showVessel = true;
                        break;
                    default:
                        showVessel = false;
                        break;
                }
            }

            if (showVessel && fcmd.cmd === FilterType.Alarms) {
                const vesselsWithAlarms = (vessel.alarms && vessel.alarms.length > 0) ? fcmd.vals!.filter(v => vessel.alarms?.find(a => a.alarmType === v)) : [];
                if (vesselsWithAlarms.length === 0) {
                    showVessel = false;
                }
            }
        });

        if (showVessel && omniFilter.text) {
            // User text input
            for (let searchWord of omniFilter.text) {
                const vesselName = vessel.vesselName || "";
                const vesselClass = vessel.vesselClass || "";
                const imo = vessel.imoNo.toString();

                const isNum = /^\d+$/.test(searchWord);

                showVessel = (
                    isNum ? imo.toString().includes(searchWord) : false ||
                        vesselName.toLowerCase().includes(searchWord) ||
                        vesselClass.toLowerCase().includes(searchWord)
                );

                // All words must be valid, so if any word is not included, break loop and don't show vessel
                if (!showVessel) {
                    break;
                }
            }
        }

        return showVessel;
    }
    
    private _sortVessels(vesselA: VesselInfo, vesselB: VesselInfo): number {
        const ranks = VoyFleetAlarmModel!.vesselRanks;

        const v1Ranks = ranks.get(vesselA.imoNo);
        const v2Ranks = ranks.get(vesselB.imoNo);

        // Handle if vessel name is null
        if (!vesselA.vesselName || !vesselB.vesselName) return 1;

        const sortedByName = vesselA.vesselName.toLowerCase().localeCompare(vesselB.vesselName.toLowerCase());

        if (!ranks) return sortedByName;

        if (!v1Ranks && v2Ranks) return 1;
        if (v1Ranks && !v2Ranks) return -1;

        // If both vessel have ranks sort them on latest alarm and rankweigth
        if (v1Ranks && v2Ranks) {
            if (v1Ranks.rankWeight === v2Ranks.rankWeight) {
                return new Date(v1Ranks!.latestAlarmTs).getTime() - new Date(v2Ranks!.latestAlarmTs).getTime();
            }

            return v1Ranks.rankWeight > v2Ranks.rankWeight ? -1 : 1;
        }

        return sortedByName;
    }

    private _filterTerminals(terminal: TerminalInfo): boolean {
        let showTerminal = true;
        const omniFilter = FilterUtils.stringToFilter(UiStateModel?.getValue('q') ?? "");
        const selectedPort = UiStateModel?.getValue(UiStateModelParam.PORT);
        const selectedRegion = UiStateModel?.getValue(UiStateModelParam.PortRegion);
        const region = selectedRegion && regionsMap.find(el => el.key.includes(selectedRegion));
        const isVesselsView = ListView.Vessels === UiStateModel!.getValue(UiStateModelParam.ListView);
        switch (true) {
            case (omniFilter && !this._omnitFilterTerminals(terminal, omniFilter) && !isVesselsView):
            case (selectedPort && !terminal.locationId?.includes(selectedPort)):
            case (selectedRegion && region && !region?.locations.find(r => r === terminal.locId)):
                showTerminal = false;
                break;
            default:
                break;
        }

        return showTerminal;
    }

    private _omnitFilterTerminals(terminal: TerminalInfo, omniFilter: VoyageFilterType): boolean {
        let showTerminal = true;

        omniFilter.text?.forEach(f => {
            showTerminal = terminal.city?.toLowerCase().includes(f) || terminal.cityId?.toLowerCase().includes(f) || f.toLowerCase().includes(terminal.cityId?.toLowerCase());
        });

        return showTerminal;
    }

    /**
     * _plotAndTrackVessels
     * used to detect any UiStateChanges to IMO, then position the map
     * with the vessel in center and display vessel's track.
     */
    private _plotAndTrackVessel(): void {
        const imo = UiStateModel!.getValue(UiStateModelParam.IMO);
        if (!imo) return;

        const scheduleDates = VoyageSchedulesModel?.getDatesFromSchedule(parseInt(imo));

        VesselsModel?.plotVessel(parseInt(imo));
        VoyagePlansModel?.syncPlans(parseInt(imo), scheduleDates?.toDate!);
    }

    public setClearFilters(): void {
        this.dispatchEvent(new Event(ShoreContextEvent.ClearFilters));
    }

    public toggleTimeLineView(): void {
        this.dispatchEvent(new Event(ShoreContextEvent.TimeLineViewToggle));
    }

    public toggleVesselSelection(): void{
        this.dispatchEvent(new CustomEvent('toggle-vessel-selection'));
    }
}
